import React, { useState, useEffect, useCallback } from "react";
import SampleIssueGeneral from "../sampleIssue/SampleIssueGeneral";
import { Box, IconButton, Tab, ThemeProvider, Tooltip } from "@mui/material";
import {
  DOCUMENT_TYPES,
  fetchDocumentNumber,
  fetchUnitOptionsForItems,
  getDocumentConfig,
  MODIFICATION_TYPES,
  theme,
  tooltipContent,
} from "../config/DocumentConfig";
import { HelpOutline } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Loader, Printer, Save, View } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { getTodayDate } from "../../utils/constants";
import { purchaseData } from "../salesInvoice/data";
import { calculateRowValues } from "../config/SalesInvoiceTableConfig";
import TimelineTab from "../salesInvoice/TimelineTab";
import ModificationCommentModal from "../salesInvoice/ModificationCommentModal";
import Modal from "../common/CommonModal";
import SummaryTotalCard from "../salesInvoice/SummaryTotalCard";
import ConfigCard from "../salesInvoice/ConfigCard";
import { calculateSampleIssueRow } from "../config/SampleIssueTableConfig";
import { getWorkOrderVatPercent } from "../config/WorkOrderTableConfig";
import SampleIssueTable from "../sampleIssue/SampleIssueTable";
import ModeIndicator from "../salesInvoice/ModeIndicator";

const SampleReturn = ({ documentType = DOCUMENT_TYPES.SAMPLE_RETURN }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useApi();
  const config = getDocumentConfig(documentType);

  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "DOODDEMO";
  const CURRENT_USER = localStorage?.getItem("currentUserName") || "admin";
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const todayDate = getTodayDate();
  const documentNoFromState = location?.state?.docNo || null;
  const modeFromState = location?.state?.mode || "new";
  const transferredData = location?.state;
  // console.log("Transferred Data to Sample Issue:", transferredData);

  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState(modeFromState);
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString(),
  );
  const [parentTabValue, setParentTabValue] = useState("1");
  const [items, setItems] = useState(purchaseData);
  const [currentLocation, setCurrentLocation] = useState(
    localStorage.getItem("departmentLocation") || "A",
  );
  const [showSidebar, setShowSidebar] = useState(false);
  const [initialRowUnitOptions, setInitialRowUnitOptions] = useState({});
  const [glSettings, setGlSettings] = useState([]);
  const [cashAccount, setCashAccount] = useState("");
  const [cashAccountDesc, setCashAccountDesc] = useState("");

  const [discountAmount, setDiscountAmount] = useState("");
  const [roundOff, setRoundOff] = useState("");

  const [autoVatIsOn, setAutoVatIsOn] = useState(true);
  const [vatAmountManual, setVatAmountManual] = useState(null);
  const [transferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isModificationModalOpen, setIsModificationModalOpen] = useState(false);

  const [drAccount, setDrAccount] = useState("");
  const [drDesc, setDrDesc] = useState("");
  const [crAccount, setCrAccount] = useState("");
  const [crDesc, setCrDesc] = useState("");

  const handleParentTabChange = (event, newValue) => {
    setParentTabValue(newValue);
  };

  const [documentData, setDocumentData] = useState({
    documentNo: "",
    date: todayDate,
    proposalNo: "",
    salesperson: "",
    salespersonId: "",
    salespersonAcc: "",
    salespersonAccDesc: "",
    customer: "",
    customerAcc: "",
    remarks: "",
    discountAmount: 0,
    roundOff: 0,
    taxableAmount: 0,
    vatAmount: 0,
    grossAmount: 0,
    netAmount: 0,
    bcAmt: 0,
    vatStatus: "",
    vatPercent: 0,
    approval: "Pending",
    currentStatus: "Normal",
    cordinatorid: "",
    cordinatorName: "",
    sampleIssueNo: "",
    projectId: "",
  });

  const calculateTotals = () => {
    const round = (val) => Math.round(Number(val) * 100) / 100;

    const processedItems = items.map((item) =>
      calculateRowValues(item, documentData, {
        items,
      }),
    );

    const rowTaxableSum = round(
      processedItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    );

    const rowVatSum = round(
      processedItems.reduce(
        (sum, item) => sum + (Number(item.vatAmount) || 0),
        0,
      ),
    );
    const grossAmount = round(
      processedItems.reduce((sum, item) => sum + (Number(item.total) || 0), 0),
    );

    const globalDiscount = Number(discountAmount) || 0;
    const roundOffValue = Number(roundOff) || 0;

    const taxableAmount = round(rowTaxableSum - globalDiscount - roundOffValue);

    const totalVat =
      !autoVatIsOn && vatAmountManual !== null
        ? round(vatAmountManual)
        : rowVatSum;

    const totalAmount = round(taxableAmount + totalVat);

    const netAmount = round(totalAmount);

    const currencyRate = Number(documentData.currencyRate) || 1;
    const bcAmt = round(netAmount * currencyRate);

    return {
      grossAmount: grossAmount.toFixed(2),
      taxableAmount: taxableAmount.toFixed(2),
      vatAmount: totalVat.toFixed(2),
      discountAmount: globalDiscount.toFixed(2),
      roundOff: roundOffValue.toFixed(2),
      netAmount: netAmount.toFixed(2),
      bcAmt: bcAmt.toFixed(2),
    };
  };

  const totals = calculateTotals();

  const fetchGLSettings = useCallback(async () => {
    const url = `${BASE_URL}/api/CRMGLSettings/${COMPANY_CODE}/SCRN_CODE/DM/-/${DEPARTMENT}/-`;
    const siUrl = `${BASE_URL}/api/CRMGLSettings/${COMPANY_CODE}/Scrn_code/si/-/${DEPARTMENT}/-`;

    try {
      const response = await request({ url, method: "GET", timeout: 10000 });
      const response2 = await request({ url: siUrl, method: "GET", timeout: 10000 });

      const settings = response || [];
      const siSettings = response2 || [];

      setGlSettings(settings);

      const credit = settings?.find((item) => item.Scrn_code === "DM-STK-C");
      if (credit) {
        setCrAccount(credit.ACCOUNT || "");
        setCrDesc(credit.accdesc || "");
      }

      const siCashAcc = siSettings?.find((item) => item.Scrn_code === "SI-CA-D");
      if (siCashAcc) {
        setCashAccount(siCashAcc.ACCOUNT || "");
        setCashAccountDesc(siCashAcc.accdesc || "");
      }
    } catch (error) {
      console.error("Failed to fetch GL settings", error);
      setGlSettings([]);

      // Reset if fetch fails
      setCrAccount("");
      setCrDesc("");
    }
  }, [BASE_URL, COMPANY_CODE]);

  useEffect(() => {
    if (mode === "new") {
      fetchGLSettings();
    }
  }, [mode, fetchGLSettings]);

  // useEffect(() => {
  //   if (documentData.salespersonAcc) {
  //     setDrAccount(documentData.salespersonAcc);
  //   }
  // }, [documentData.salespersonAcc]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Validate items
      const filteredItems = items.filter(
        (item) => item.code && item.code.trim() !== "",
      );

      if (filteredItems.length === 0) {
        alert("Please add at least one item to the sample return");
        setIsSaving(false);
        return;
      }

      if (!documentData.customer) {
        alert("Please select a customer");
        setIsSaving(false);
        return;
      }

      if (!documentData.proposalNo) {
        alert("Proposal number is required");
        setIsSaving(false);
        return;
      }

      // Calculate totals before saving
      const round = (val) => Math.round(Number(val) * 100) / 100;

      // Map line items to payload format
      const lineItemsPayload = filteredItems.map((item, index) => ({
        Slno: index + 1,
        code: item.code || "",
        description: item.description || "",
        locn: item.location || "A",
        unit: item.unit || "PCS",
        qty: Number(item.qty) || 0,
        "Unit Price": Number(item.unitPrice) || 0,
        "Disc%": Number(item.discountPercent) || 0,
        Ldisc_amt:
          Number(item.qty) *
          Number(item.unitPrice) *
          (Number(item.discountPercent) / 100) || 0,
        Amount: Number(item.amount) || 0,
        x: "",
        cntrl: item.cntrl || "",
        Fraction: item.fraction || "1",
        "vat%": Number(item.vatPercent) || 0,
        "Unit Cost": Number(item.unitCost) || 0,
        dono: "0",
        sono: "0",
        quotno: "0",
        Avlqty: "0",
        Vatamt: Number(item.vatAmount) || 0,
        BDiscsplit: "0",
        total: Number(item.total) || 0,
        oem: item.oem || "",
        Bin: "",
        rcvd_mode1: "",
        rcvd_mode1_acc: drAccount || "",
        rcvd_mode1_amt: 0,
        rcvd_mode2: "",
        rcvd_mode2_acc: "",
        rcvd_mode2_amt: 0,
        commi_acc_Dr: "",
        commi_acc_Cr: "",
        commi_amt: 0,
        shipping_type: "",
        shipping_amt: 0,
      }));

      const masterPayload = {
        cmpcode: COMPANY_CODE,
        modeop: mode === "edit" ? "EDIT" : "SAVE",
        cashcustomer: "",
        inv_no: documentData.documentNo || "",
        cust_acc: crAccount || "",
        inv_total: totals.netAmount || "0",
        inv_cost: "0",
        jv_num: "0",
        comments: documentData.remarks || "",
        disc_amt: documentData.discountAmount || "0",
        sale_man: documentData.salespersonId || "",
        lpo_no: documentData.proposalNo || "",
        do_no: "0",
        due_date: documentData.date || todayDate,
        area_code: documentData.areaCode || "",
        type: "R",
        do_data: documentData.cordinatorid || "",
        q_no: "0",
        inv_type: "",
        user_id: CURRENT_USER,
        fdisc_amt: "0",
        costc: "",
        job_code: "",
        inv_fctota: totals.netAmount || "0",
        fc: documentData.currency || "AED",
        awb: "",
        blno: "",
        pkg: "",
        rate2: "0",
        rate: Number(documentData.currencyRate) || 1,
        invcurr: "",
        supl_code: "",
        status: "",
        tr_refno: documentData.proposalNo || "",
        payment: documentData.paymentTerms || "cash",
        manuf: "",
        origin: documentData.origin || "",
        shipment: documentData.shipment || "",
        delivery: documentData.delivery || "",
        packing: "",
        netwt: "",
        grosswt: "",
        insurance: documentData.projectId || "",
        custom: "",
        validity: "",
        foot1: documentData.delivery || "",
        frdet: "",
        fruprice: "0",
        framt: "0",
        fob: "",
        repeat: "",
        comm: "0",
        date_paid: documentData.date || todayDate,
        commdue: "0",
        comm_paid: "0",
        commp: "0",
        type_no: documentData.customerAcc || "",
        paid_amt: "0",
        client_pd: documentData.vatStatus || "VAT Applied",
        tel: documentData.tel || "",
        custref: documentData.customer || "",
        terms: "0",
        deptno: DEPARTMENT,
        month: "",
        cashcred: "",
        inv_date: documentData.date || todayDate,
        so_no: "0",
        sys_date: todayDate,
        comm_pd_dt: documentData.date || todayDate,
        pump: "",
        governor: "",
        feedpump: "",
        injector: "",
        starter: "",
        enginetype: "CASH",
        date_prom: documentData.date || todayDate,
        date_delvd: documentData.date || todayDate,
        inv_qty: "0",
        rcvd_amt: "0",
        time: "",
        lab_charge: "0",
        oth_charge: 0,
        s: "",
        m: "0",
        d: "0",
        e: "0",
        w: "0",
        wa: "0",
        pgroup: "",
        upd: "",
        do_date: documentData.date || todayDate,
        lpo_date: documentData.date || todayDate,
        vehicleno: "",
        drivername: "",
        deliv_site: "",
        head1: "",
        cap: "DM-R",
        salesacc: drAccount || "",
        salesAccDesc: drDesc || "",
      };

      const finalPayload = lineItemsPayload.map((item) => ({
        ...masterPayload,
        ...item,
      }));
      console.log("Sample Issue Payload to save:", finalPayload);
      // return
      const apiUrl = `${BASE_URL}${config.apiEndpoints.save}`;

      const response = await request({
        url: apiUrl,
        method: "POST",
        data: finalPayload,
        timeout: 10000,
      });

      console.log("API Response:", response);

      // Handle success response
      if (response && response.result === "Saved") {
        const issueNumber = response.invoiceNo;
        alert(`Sample Return #${issueNumber} saved successfully!`);

        navigate("/sample_return_list");
      } else if (response && response.result) {
        alert(`Sample Return saved successfully!`);
        navigate("/sample_return_list");
      } else {
        alert(
          `Failed to save Sample Return: ${response?.message || "Unknown error"
          }`,
        );
      }
    } catch (error) {
      console.error("Error saving Sample Return:", error);
      alert("Error saving Sample Return: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };
  const handleTransfer = async (docType, selectedRows) => {
    setIsTransferModalOpen(false);
    console.log("Selected rows from Parent component", selectedRows);
    const docNos = selectedRows
      .map((row) => {
        return row.ISSUE_NO;
      })
      .join(",");
    console.log("Doc number transfered", docNos);
    const masterUrl = `${BASE_URL}${config.apiEndpoints.transfer.master(
      COMPANY_CODE,
      DEPARTMENT,
      docNos,
      docType,
    )}`;
    const detailUrl = `${BASE_URL}${config.apiEndpoints.transfer.detail(
      COMPANY_CODE,
      DEPARTMENT,
      docNos,
      docType,
    )}`;

    try {
      const masterRes = await fetch(masterUrl);
      const masterData = await masterRes.json();
      console.log("Master Transfer Response:", masterData);

      if (masterData && masterData.length > 0) {
        const totalDiscount = masterData?.reduce(
          (acc, row) => acc + (row.so_disc || 0),
          0,
        );
        const main = masterData[0];
        setDocumentData((prev) => ({
          ...prev,
          // documentNo: String(
          //   main[config.fieldMappings.documentNo] || main.inv_no || ""
          // ),
          proposalNo: main.lpo_no || main.Expr3 || "",
          customer: main.custref || "",
          customerAcc: main.type_no || "",
          salesperson: main.sale_man || "",
          salespersonId: main.sale_man || "",
          salespersonAcc: main.salesacc || "",
          salespersonAccDesc: main.salesAccDesc || "",
          date: main.inv_date ? main.inv_date.slice(0, 10) : todayDate,
          vatStatus: main.deliv_site || prev.vatStatus,
          cordinatorid: main.do_data || "",
          cordinatorName: main.cordinatorName || prev.cordinatorName || "",
          proposalNo: main.inv_no || "",
          projectId: main.insurance || "",

        }));

        setDrAccount(main.cust_acc || "");
        setDrDesc(main.DrAccDesc || "");
        setCrAccount(main.salesacc || "");
        setCrDesc(main.salesAccDesc || "");
        setDiscountAmount(main.disc_amt || 0);
        setRoundOff(main.e || 0);
      }

      const detailRes = await fetch(detailUrl);
      const detailData = await detailRes.json();
      console.log("Detail Transfer Response:", detailData);
      // Item mapping for transfered data
      if (detailData && detailData.length > 0) {
        const items = detailData.map((row, idx) => ({
          id: idx + 1,
          sino: idx + 1,
          code: row.code || row.po_icode || row.so_icode || row.idesc,
          description: row.idesc,
          location: row.po_locn || row.locn,
          unit: row.unit?.trim(),
          qty: row.tr_qty2 || row.so_qty || 0,
          unitPrice: row.unit_price || row.so_fccost || 0,
          unitCost: row.unit_price || row.so_fccost,
          UnitCost: String(Number(row.unit_price) || row.so_fccost || 0),
          unitprice: String(Number(row.unit_price) || row.so_fccost || 0),
          discount: row.po_fdisc || row.so_disc || row.so_fdisc,
          fraction: row.fraction,
          cntrl: row.soGuid || "",
        }));

        const initialRowUnitOptions = await fetchUnitOptionsForItems(
          items,
          BASE_URL,
          COMPANY_CODE,
        );
        setItems(items);
        setInitialRowUnitOptions(initialRowUnitOptions);
      }
    } catch (err) {
      console.error("Error fetching transfer data:", err);
    }
  };
  const handlePrint = () => { };
  const fetchDocumentDetail = async (documentNo) => {
    try {
      const url = config.apiEndpoints.detail(
        COMPANY_CODE,
        documentNo,
        DEPARTMENT,
      );
      const fullUrl = `${BASE_URL}${url}`;
      const response = await request({ url: fullUrl, method: "GET" });

      if (response && response.length > 0) {
        const main = response[0];
        console.log("Fetched Sample Return detail:", main);

        setDocumentData((prev) => ({
          ...prev,
          documentNo: main.inv_no || "",
          proposalNo: main.lpo_no || main.Expr3 || "",
          customer: main.custref || "",
          customerAcc: main.type_no || "",
          salesperson: main.sale_man || "",
          salespersonId: main.sale_man || "",
          salespersonAcc: main.salesacc || "",
          salespersonAccDesc: main.salesAccDesc || "",
          date: main.inv_date ? main.inv_date.slice(0, 10) : todayDate,
          vatStatus: main.deliv_site || prev.vatStatus,
          cordinatorid: main.do_data || "",
          cordinatorName: main.cordinatorName || prev.cordinatorName || "",
          sampleIssueNo: main.tr_refno || main.lpo_no || "",
          projectId: main.insurance || "",

        }));

        setCrAccount(main.cust_acc || "");
        setCrDesc(main.DrAccDesc || "");
        setDrAccount(main.salesacc || "");
        setDrDesc(main.salesAccDesc || main.ccno || "");
        setDiscountAmount(main.disc_amt || 0);
        setRoundOff(main.e || 0);

        const mappedItems = response.map((row, idx) => ({
          id: idx + 1,
          sino: idx + 1,
          code: row.code || row.do_icode || "",
          description: row.idesc || "",
          location: row.locn || currentLocation || "MAIN SHOP",
          unit: row.unit || row.punit || "",
          qty: row.tr_qty2 || row.tr_qty || row.so_qty || 0,
          unitPrice: row.funit_pric || row.unit_price || row.unit_cost || 0,
          unitCost: row.funit_pric || row.unit_price || row.unit_cost || 0,
          discount: row.discount || 0,
          fraction: row.fraction != null ? String(row.fraction) : "1",
          "vat%": row.bcdisc || 0,
          cntrl: row.soGuid || "",
          amount: row.line_total || row.fline_tota || 0,
        }));

        setItems(mappedItems);
      }
    } catch (err) {
      console.error("Failed to fetch Sample return detail:", err);
    }
  };
  const initializeDocument = useCallback(async () => {
    try {
      const { voucherType, documentNo } = await fetchDocumentNumber(
        documentType,
        documentData.paymentMode,
        BASE_URL,
        COMPANY_CODE,
        DEPARTMENT,
      );

      if (documentNo) {
        setDocumentData((prev) => ({
          ...prev,
          documentNo: documentNo,
          voucherType,
        }));
      }
      console.log("Voucher Type:", voucherType, documentNo);
    } catch (err) {
      console.error("Error initializing document:", err);
    }
  }, [
    documentType,
    documentData?.paymentMode,
    BASE_URL,
    COMPANY_CODE,
    DEPARTMENT,
  ]);

  useEffect(() => {
    if (documentNoFromState && request) {
      fetchDocumentDetail(documentNoFromState);
    } else {
      // keep existing behavior for new mode
      setDocumentData((prev) => ({ ...prev, date: todayDate }));
    }
  }, [documentNoFromState]);

  useEffect(() => {
    if (mode === "new") {
      initializeDocument();
    }
  }, [mode, initializeDocument]);

  return (
    <div className="w-full relative">
      <div className="flex items-center justify-between lg:px-10 px-2 mb-2 gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">Sample Return</h1>
        </div>

        <Tooltip title={tooltipContent} sx={{ margin: 0, padding: 0 }}>
          <IconButton>
            <HelpOutline
              className="text-blue-600 bg-white rounded-full"
              fontSize="medium"
            />
          </IconButton>
        </Tooltip>
      </div>
      <ModeIndicator mode={mode} documentType={documentType} />
      <div className="w-full rounded-lg">
        <div className="lg:px-10">
          <div className="bg-gray-50 border lg:p-3 rounded-lg shadow pb-4">
            <ThemeProvider theme={theme}>
              <TabContext value={parentTabValue}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TabList onChange={handleParentTabChange}>
                    <Tab label="General" value="1" sx={{ padding: 0 }} />
                    <Tab label="Timeline" value="2" sx={{ padding: 0 }} />
                  </TabList>
                  <div className="lg:flex hidden items-center gap-3">
                    <button
                      onClick={() => {
                        navigate("/sample_return_list");
                      }}
                      className="flex items-center gap-2 px-5 py-2.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg shadow hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <View className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={() => {
                        if (mode === "edit") {
                          setIsModificationModalOpen(true);
                        } else {
                          handleSave();
                        }
                      }}
                      disabled={mode === "view" || isSaving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          {mode === "edit" ? "Update" : "Save"}
                        </>
                      )}
                    </button>
                  </div>
                </Box>
                <TabPanel value="1" sx={{ p: 0, paddingX: 0 }}>
                  <SampleIssueGeneral
                    WorkOrderData={documentData}
                    setWorkOrderData={setDocumentData}
                    drAccount={drAccount}
                    setDrAccount={setDrAccount}
                    drDesc={drDesc}
                    setDrDesc={setDrDesc}
                    crAccount={crAccount}
                    setCrAccount={setCrAccount}
                    crDesc={crDesc}
                    setCrDesc={setCrDesc}
                    BASE_URL={BASE_URL}
                    COMPANY_CODE={COMPANY_CODE}
                    DEPARTMENT_NO={DEPARTMENT}
                    type="SAMPLERETURN"
                    cashAccount={cashAccount}
                    cashAccountDesc={cashAccountDesc}
                  />
                </TabPanel>

                <TabPanel value="2" sx={{ p: 1 }}>
                  <TimelineTab
                    BASE_URL={BASE_URL}
                    DEPARTMENT={DEPARTMENT}
                    modificationType={MODIFICATION_TYPES[documentType] || null}
                    invoiceId={documentNoFromState}
                    COMPANY_CODE={COMPANY_CODE}
                  />
                </TabPanel>
              </TabContext>
            </ThemeProvider>
          </div>
        </div>
        <main className="w-full mt-4 relative">
          <SampleIssueTable
            items={items}
            onItemsChange={setItems}
            setShowSidebar={setShowSidebar}
            setCurrentLocation={setCurrentLocation}
            extraFields={[]}
            purchaseData={documentData}
            initialRowUnitOptions={initialRowUnitOptions}
            setInitialRowUnitOptions={setInitialRowUnitOptions}
            calculateWorkOrderRow={calculateSampleIssueRow}
            invoiceData={documentData}
            getVatPercent={getWorkOrderVatPercent}
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            DEPARTMENT_NO={DEPARTMENT}
          />
        </main>
        <div className="w-full lg:px-10 p-1 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <ConfigCard
              invoiceData={documentData}
              setAutoVatIsOn={setAutoVatIsOn}
              setInvoiceData={setDocumentData}
              autoVatIsOn={autoVatIsOn}
              documentType={documentType}
              config={config}
              setIsModalOpen={setIsTransferModalOpen}
              isModalOpen={transferModalOpen}
              invoiceNoFromState={documentNoFromState}
              accountData={{
                account: drAccount,
              }}
              onTransfer={handleTransfer}
            />
            {(() => {
              let userAccess = "";
              try {
                const userObj = JSON.parse(localStorage.getItem("currentUser"));
                userAccess = userObj?.[0]?.useraccess || "";
              } catch (e) { }
              const shouldHideAmount = ["AC", "SW", "ST"].some((code) =>
                userAccess.includes(code),
              );
              if (shouldHideAmount) return null;
              return (
                <SummaryTotalCard
                  totals={totals}
                  setInvoiceData={setDocumentData}
                  invoiceData={documentData}
                  handlePrint={handlePrint}
                  handleSave={handleSave}
                  roundOff={roundOff}
                  setRoundOff={setRoundOff}
                  discountAmount={discountAmount}
                  setDiscountAmount={setDiscountAmount}
                  mode={mode}
                  autoVatIsOn={autoVatIsOn}
                  setVatAmountManual={setVatAmountManual}
                  config={config}
                  setIsModificationModalOpen={setIsModificationModalOpen}
                />
              )
            })}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModificationModalOpen}
        onClose={() => setIsModificationModalOpen(false)}
        size="md"
        header={true}
        title="Update Confirmation"
        showFullScreen={false}
      >
        <ModificationCommentModal
          isOpen={isModificationModalOpen}
          onClose={() => setIsModificationModalOpen(false)}
          handleSave={handleSave}
          documentData={documentData}
          documentType={documentType}
          COMPANY_CODE={COMPANY_CODE}
          DEPARTMENT={DEPARTMENT}
          CURRENT_USER={CURRENT_USER}
          BASE_URL={BASE_URL}
          setIsModificationModalOpen={setIsModificationModalOpen}
        />
      </Modal>
    </div>
  );
};

export default SampleReturn;
